import React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  ScanLine,
  ThumbsUp,
  XCircle,
} from "lucide-react";
import { useI18n } from "../i18n";
import { cn } from "../lib/utils";

export const STATUS_META = {
  queued: { icon: Clock, cls: "bg-slate-100 text-slate-700 border-slate-200" },
  processing: { icon: Loader2, cls: "bg-blue-50 text-blue-700 border-blue-200", spin: true },
  parsed: { icon: ScanLine, cls: "bg-teal-50 text-teal-800 border-teal-200" },
  needs_review: { icon: AlertTriangle, cls: "bg-amber-50 text-amber-800 border-amber-200" },
  failed: { icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200" },
  approved: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { icon: XCircle, cls: "bg-red-50 text-red-700 border-red-200" },
  uploading: { icon: Loader2, cls: "bg-blue-50 text-blue-700 border-blue-200", spin: true },
};

export const StatusBadge = ({ status, className, size = "sm" }) => {
  const { t } = useI18n();
  const meta = STATUS_META[status] || {
    icon: ThumbsUp,
    cls: "bg-slate-100 text-slate-700 border-slate-200",
  };
  const Icon = meta.icon;
  const label = STATUS_META[status] ? t(`status.${status}`) : status || "—";
  return (
    <span
      data-testid="receipt-status-badge"
      data-status={status}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border font-medium whitespace-nowrap",
        size === "sm" ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm",
        meta.cls,
        className
      )}
    >
      <Icon className={cn("h-3.5 w-3.5", meta.spin && "animate-spin")} aria-hidden="true" />
      {label}
    </span>
  );
};

export const ConfidenceMeter = ({ value, className }) => {
  const pct = Math.round((Number(value) || 0) * 100);
  const tone = pct >= 85 ? "bg-emerald-500" : pct >= 75 ? "bg-teal-500" : "bg-amber-500";
  return (
    <div className={cn("flex items-center gap-2", className)} data-testid="receipt-confidence">
      <div className="h-1.5 w-14 overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full rounded-full", tone)} style={{ width: `${pct}%` }} />
      </div>
      <span className="ll-num text-xs text-muted-foreground">{pct}%</span>
    </div>
  );
};

export default StatusBadge;

/** Small pill showing whether an employee refund has been paid out. */
export const PayoutBadge = ({ status, className = "" }) => {
  const { t } = useI18n();
  const paid = status === "paid";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
        paid
          ? "bg-primary/10 text-primary"
          : "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
      } ${className}`}
      data-testid={paid ? "payout-badge-paid" : "payout-badge-unpaid"}
    >
      {paid ? t("receipts.paidBadge") : t("receipts.unpaidBadge")}
    </span>
  );
};
